import { useState } from "react";
import { Trash2 } from "lucide-react";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzWorkOrderRemoveAssessmentMutation } from "@/services/backend/htz/work-order/api/work-order-api";
import {
  Position,
  positionFieldName,
} from "@/services/backend/htz/work-order/types/work-order";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";

// The lists of the position the dialog removes assessments from.
const assessmentLists = [
  "structuralAssessments",
  "visualAssessments",
  "microbialAssessments",
  "photos",
];

/**
 * RemoveAssessmentDialog removes an assessment of any kind from the work
 * order position. The file of a photo is deleted with it.
 */
export function RemoveAssessmentDialog({
  position,
  assessmentId,
  title,
  description,
  onRemoved,
  disabled,
}: {
  position: Position;
  assessmentId: string;
  title: string;
  description: string;
  onRemoved?: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [remove, { isLoading, error, reset }] =
    useHtzWorkOrderRemoveAssessmentMutation();
  const { resetFields } = useErrValidationFailedContext();

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    remove({ id: position.workOrderId, positionId: position.id, assessmentId })
      .unwrap()
      .then(() => {
        assessmentLists.forEach((list) =>
          resetFields(positionFieldName(position, list, assessmentId)),
        );
        reset();
        setOpen(false);
        onRemoved?.();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger
        icon={Trash2}
        label=""
        variant="ghost"
        size="sm"
        disabled={disabled}
      />
      <DialogContent>
        <CommonDialogHeader title={title} description={description} />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
